import { useEffect, useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  return online;
}

function useNewVersionAvailable() {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const check = async () => {
      if (!navigator.onLine) return;
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) return;
        const data: unknown = await response.json();
        const version = (data as { version?: unknown })?.version;
        if (typeof version === 'string' && version !== __APP_VERSION__) setAvailable(true);
      } catch {
        // Sem rede ou resposta inválida: tenta de novo no próximo ciclo.
      }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    check();
    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return available;
}

export default function AppStatusBanners() {
  const online = useOnlineStatus();
  const updateAvailable = useNewVersionAvailable();

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={`fixed inset-x-0 top-0 z-[70] flex justify-center px-4 transition-all duration-300 ${online ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 8px)' }}
      >
        <div className="flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-lg">
          <WifiOff className="h-4 w-4 text-amber-400" />
          Sem conexão com a internet
        </div>
      </div>

      {updateAvailable && (
        <div
          className="fixed inset-x-0 bottom-0 z-[70] flex justify-center px-4 animate-[app-slide-up_300ms_ease-out]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
        >
          <div className="flex w-full max-w-md items-center gap-3 rounded-2xl bg-[#0f1b2d] p-3 pl-4 text-white shadow-2xl ring-1 ring-white/10">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-amber-500/15">
              <RefreshCw className="h-4 w-4 text-amber-400" />
            </div>
            <p className="flex-1 text-sm font-medium leading-snug">Nova versão disponível</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-[#0f1b2d] transition-colors hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              Atualizar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
